import {useApp} from '../../context/AppContext'
import {getStatusConfig} from '../../utils/helpers'
import type {Task} from '../../types'
import DonutChart from './DonutChart'

export default function TaskDistribution({
  tasks: scopedTasks,
  boardId,
}: {
  tasks?: Task[]
  boardId?: string
}) {
  const {state} = useApp()
  const tasks = scopedTasks || state.tasks
  const boards = boardId ? state.boards.filter((board) => board.id === boardId) : state.boards

  const allStatuses = [
    ...new Set([
      ...boards.flatMap((b) => b.columns.map((c) => c.status)),
      ...tasks.map((task) => task.status),
    ]),
  ]

  const distribution = allStatuses.map((status) => {
    const count = tasks.filter((t) => t.status === status).length
    const column = boards.flatMap((board) => board.columns).find((item) => item.status === status)
    const config = {
      ...getStatusConfig(status),
      ...(column ? {label: column.title, accent: column.color} : {}),
    }
    return {status, count, ...config}
  })

  const maxCount = Math.max(...distribution.map((d) => d.count), 1)

  const priorityDist = [
    {
      key: 'critical',
      label: 'Crítica',
      count: tasks.filter((t) => t.priority === 'critical').length,
      color: '#ef4444',
      bg: '#fef2f2',
      text: '#b91c1c',
    },
    {
      key: 'high',
      label: 'Alta',
      count: tasks.filter((t) => t.priority === 'high').length,
      color: '#f97316',
      bg: '#fff7ed',
      text: '#c2410c',
    },
    {
      key: 'medium',
      label: 'Média',
      count: tasks.filter((t) => t.priority === 'medium').length,
      color: '#f59e0b',
      bg: '#fffbeb',
      text: '#b45309',
    },
    {
      key: 'low',
      label: 'Baixa',
      count: tasks.filter((t) => t.priority === 'low').length,
      color: '#94a3b8',
      bg: '#f8fafc',
      text: '#475569',
    },
  ]

  const totalTasks = tasks.length || 1

  const priorityChartSegments = priorityDist.map((p) => ({
    label: p.label,
    value: p.count,
    color: p.color,
  }))

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
      {/* STATUS */}
      <div className='devops-surface p-4 md:p-5'>
        <div className='flex items-center justify-between mb-4'>
          <div>
            <h3 className='text-sm font-semibold text-slate-700'>Tarefas por Status</h3>
            <p className='text-[11px] text-slate-500 mt-0.5'>
              Visão geral + detalhamento por etapa
            </p>
          </div>

          <span className='text-[11px] text-slate-500'>
            Total: <strong className='text-slate-700'>{tasks.length}</strong>
          </span>
        </div>

        {/* NOVO GRÁFICO DE BARRAS VERTICAIS */}
        <div className='rounded-xl border border-slate-200 bg-slate-50/60 p-3 mb-4'>
          <div
            className='flex items-end gap-3 h-44 overflow-x-auto'
            aria-label='Distribuição por etapa'
          >
            {distribution.map((item) => {
              const percentage =
                tasks.length > 0 ? Math.round((item.count / tasks.length) * 100) : 0
              const height = `${Math.max((item.count / maxCount) * 100, item.count > 0 ? 10 : 2)}%`

              return (
                <div
                  key={item.status}
                  className='flex-1 min-w-[72px] h-full flex flex-col items-center justify-end gap-2'
                  title={`${item.label}: ${item.count} tarefas`}
                >
                  <span className='text-[11px] font-semibold text-slate-700'>{item.count}</span>

                  <div className='w-full max-w-[56px] h-28 sm:h-32 flex items-end'>
                    <div
                      className='w-full rounded-t-xl transition-all duration-700 ease-out shadow-sm'
                      style={{
                        height,
                        background: `linear-gradient(to top, ${item.accent}, ${item.accent}CC)`,
                      }}
                    />
                  </div>

                  <div className='text-center'>
                    <p className='text-[11px] font-medium text-slate-700 truncate max-w-full'>
                      {item.label}
                    </p>
                    <p className='text-[10px] text-slate-400'>{percentage}%</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/*}
        <div className="space-y-3">
          {distribution.map((item) => {
            const percentage = tasks.length > 0 ? Math.round((item.count / tasks.length) * 100) : 0;

            return (
              <div
                key={item.status}
                className="rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-3"
              >
                <div className="flex items-center justify-between mb-2 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: item.accent }}
                    />
                    <span className="text-sm font-medium text-slate-700 truncate">
                      {item.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{percentage}%</span>
                    <span className="text-sm font-bold text-slate-700">{item.count}</span>
                  </div>
                </div>

                <div className="h-2.5 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${(item.count / maxCount) * 100}%`,
                      backgroundColor: item.accent,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>*/}
      </div>

      {/* PRIORIDADE */}
      <div className='devops-surface p-4 md:p-5'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-sm font-semibold text-slate-700'>Tarefas por Prioridade</h3>
          <span className='text-[11px] text-slate-500'>
            Total: <strong className='text-slate-700'>{tasks.length}</strong>
          </span>
        </div>

        <div className='flex flex-col sm:flex-row items-center gap-5'>
          <DonutChart
            segments={priorityChartSegments}
            size={140}
            strokeWidth={20}
            centerValue={tasks.length}
            centerLabel='tarefas'
          />

          <div className='flex-1 w-full space-y-2'>
            {priorityDist.map((item) => {
              const percentage = Math.round((item.count / totalTasks) * 100)

              return (
                <div
                  key={item.key}
                  className='rounded-lg border border-slate-200 px-3 py-2'
                  style={{backgroundColor: item.bg}}
                >
                  <div className='flex items-center justify-between gap-3 mb-1.5'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <div
                        className='w-2.5 h-2.5 rounded-full shrink-0'
                        style={{backgroundColor: item.color}}
                      />
                      <span className='text-xs font-semibold truncate' style={{color: item.text}}>
                        {item.label}
                      </span>
                    </div>

                    <div className='flex items-center gap-2 shrink-0'>
                      <span className='text-[11px] font-medium text-slate-500'>{percentage}%</span>
                      <span className='text-xs font-bold text-slate-700'>{item.count}</span>
                    </div>
                  </div>

                  <div className='h-1.5 bg-white/80 rounded-full overflow-hidden'>
                    <div
                      className='h-full rounded-full transition-all duration-700 ease-out'
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
